
import React from "react";
import { ArrowLeft, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ArticleStatus } from "@/types/article-types";

interface ArticleFormHeaderProps {
  isEditMode: boolean;
  onCancel: () => void;
  title?: string; // Added title prop
  status?: ArticleStatus;
  lastSaved?: Date;
  publishDate?: Date | undefined;
}

const ArticleFormHeader = ({ 
  isEditMode, 
  onCancel,
  title = "",  // Default value
  status = "draft",
  lastSaved,
  publishDate
}: ArticleFormHeaderProps) => {
  // Helper function to get badge styling based on article status
  const getStatusBadge = () => {
    switch(status) {
      case "published":
        return <Badge className="ml-2 bg-green-500">Published</Badge>;
      case "scheduled":
        return <Badge className="ml-2 bg-blue-500">Scheduled</Badge>;
      case "draft":
      default:
        return <Badge className="ml-2 bg-yellow-500">Draft</Badge>;
    }
  };

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="rounded-full"
          aria-label="Back to articles"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">
              {isEditMode ? "Edit Article" : "Create New Article"}
              {title && `: ${title}`} {/* Display title if provided */}
            </h1>
            {isEditMode && getStatusBadge()}
          </div>
          
          {isEditMode && publishDate && status === "scheduled" && (
            <p className="text-sm text-muted-foreground mt-1">
              Scheduled to publish on {format(new Date(publishDate), "PPP 'at' p")}
            </p>
          )}
        </div>
      </div>
      
      {lastSaved && (
        <div className="flex items-center text-sm text-muted-foreground ml-14">
          <Clock className="h-3 w-3 mr-1" />
          Last saved: {format(lastSaved, "MMM d, yyyy 'at' h:mm a")}
        </div>
      )}
    </div>
  );
};

export default ArticleFormHeader;
